/** Khung chi tiết một người ở thẻ «Tình hình kinh doanh»: ô số, đơn trong tháng, khách quá hẹn gọi lại. */
import { Link } from "@tanstack/react-router";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { formatVND } from "@/lib/format";
import { statusLabel } from "@/features/reports/constants";
import { SheetDescription, SheetHeader, SheetTitle } from "@/ui/sheet";
import { type SalesTeamOverviewNode, useSalesTeamOverviewPerson } from "../api";
import { dayText, monthText } from "../labels";
import { sourceLabel } from "../overview";
import { MetricTiles } from "./OverviewTiles";
import { PersonMark } from "./marks";

const LEAD_STAGE_LABEL: Record<string, string> = {
  new: "Mới",
  consulting: "Đang tư vấn",
  quoted: "Đã báo giá",
};

export function PersonDetail({ node, month }: { node: SalesTeamOverviewNode; month: string }) {
  const q = useSalesTeamOverviewPerson(month, node.id);
  return (
    <div className="space-y-5">
      <SheetHeader>
        <SheetTitle>
          <PersonMark staffId={node.id} name={node.name} size={32} />
        </SheetTitle>
        <SheetDescription>Tháng {monthText(month)}</SheetDescription>
      </SheetHeader>
      <MetricTiles m={node.metrics} prev={node.prev} isPerson narrow />
      {q.isError ? (
        <QueryErrorNote error={q.error} fallback="Không tải được số của người này" />
      ) : !q.data ? (
        <p className="text-sm text-muted">Đang tải…</p>
      ) : (
        <>
          <section className="space-y-2">
            <h4 className="text-sm font-semibold">Đơn có ngày chụp hoặc khai giảng trong tháng ({q.data.orders.length})</h4>
            {q.data.orders.length === 0 ? (
              <p className="text-sm text-muted">Chưa có đơn nào.</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border text-sm">
                {q.data.orders.map((ord) => (
                  <li key={ord.id} className="flex flex-wrap items-baseline justify-between gap-2 p-2">
                    <span className="min-w-0">
                      {ord.system === "gnpm" ? (
                        <>
                          <span className="font-medium">{ord.code}</span>
                          <span className="ml-2 rounded border border-border px-1 text-xs text-muted">GNPM</span>
                        </>
                      ) : (
                        <Link to="/orders/$orderId" params={{ orderId: ord.id }} className="font-medium underline-offset-2 hover:underline">
                          {ord.code}
                        </Link>
                      )}
                      <span className="ml-2 text-muted">{ord.customer}</span>
                      <span className="block text-xs text-muted">
                        {ord.system === "gnpm"
                          ? `Khai giảng hoặc lên đơn ${dayText(ord.shootDate)}`
                          : `Chụp ${dayText(ord.shootDate)} · ${statusLabel(ord.status)}`}{" "}
                        · {sourceLabel(ord.source)}
                      </span>
                    </span>
                    <span className="tabular-nums">{formatVND(ord.revenue)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {q.data.overdueShown ? (
            <section className="space-y-2">
              <h4 className="text-sm font-semibold">Khách quá hẹn gọi lại ({q.data.overdueLeads.length})</h4>
              {q.data.overdueLeads.length === 0 ? (
                <p className="text-sm text-muted">Không có khách nào quá hẹn.</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border text-sm">
                  {q.data.overdueLeads.map((l) => (
                    <li key={l.id} className="flex flex-wrap items-baseline justify-between gap-2 p-2">
                      <span className="min-w-0 font-medium">{l.customer || "Khách chưa ghi tên"}</span>
                      <span className="text-xs text-muted">
                        Hẹn {dayText(l.followUpDate)} · {LEAD_STAGE_LABEL[l.stage] ?? l.stage}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
